import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { fadeInUp } from "@/lib/motion";
import { NavArrowLeft, NavArrowRight, MapPin } from "iconoir-react";
import { useLanguage } from "@/components/LanguageContext";
import { avisiumProjects } from "@/lib/projects";

export function ProjectsSection() {
  const { t, language } = useLanguage();
  const [current, setCurrent] = useState(0);

  // Select 6 flagship projects for the homepage carousel
  const flagshipIds = [12, 18, 5, 1, 6, 10];
  const projects = flagshipIds.map(id => avisiumProjects.find((p: any) => p.id === id)!).filter(Boolean);



  const prev = () => setCurrent((c) => (c === 0 ? projects.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === projects.length - 1 ? 0 : c + 1));

  const project = projects[current];

  return (
    <section className="py-24 lg:py-32 bg-warm-white">
      <Container>
        <SectionHeading
          overline={t("Études de Cas", "Case Studies")}
          title={t("Des Projets qui Façonnent les Territoires", "Projects That Shape Territories")}
          subtitle={t("Une sélection d'engagements démontrant notre approche intégrée de la mobilité, des infrastructures et de l'intelligence territoriale.", "Selected engagements demonstrating our integrated approach to mobility, infrastructure, and territorial intelligence.")}
        />

        <div className="relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: -20, transition: { duration: 0.3 } }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
            >
              {/* Visual side */}
              <div className="relative h-72 sm:h-80 lg:h-[420px] rounded-2xl bg-mineral overflow-hidden">
                <img
                  src={project.image}
                  alt={language === 'fr' ? project.title.fr : project.title.en}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105 mix-blend-multiply opacity-90"
                />

                {/* Project tag */}
                <div className="absolute top-5 left-5 z-10">
                  <span className="text-xs font-semibold px-4 py-1.5 rounded-full bg-charcoal text-white shadow-md">
                    {language === 'fr' ? project.category.fr : project.category.en}
                  </span>
                </div>

                {/* Project number */}
                <div className="absolute bottom-5 right-5 z-10 text-6xl font-display font-bold text-charcoal/5">
                  {String(current + 1).padStart(2, "0")}
                </div>
              </div>

              {/* Content side */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-3xl md:text-4xl font-display font-semibold text-charcoal leading-tight mb-3">
                    {language === 'fr' ? project.title.fr : project.title.en}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-graphite-light font-medium">
                    <MapPin width={16} height={16} />
                    {language === 'fr' ? project.location.fr : project.location.en}
                  </div>
                </div>

                <div className="space-y-5 pt-2">
                  <div className="bg-white/50 p-5 rounded-xl border border-mineral-dark shadow-sm">
                    <h4 className="text-xs uppercase tracking-widest text-amber font-bold mb-2 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber"></span>
                      {t("Le Défi", "The Challenge")}
                    </h4>
                    <p className="text-sm text-graphite leading-relaxed">
                      {language === 'fr' ? project.challenge.fr : project.challenge.en}
                    </p>
                  </div>

                  <div className="bg-white/50 p-5 rounded-xl border border-mineral-dark shadow-sm">
                    <h4 className="text-xs uppercase tracking-widest text-amber font-bold mb-2 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber"></span>
                      {t("Notre Approche", "Our Approach")}
                    </h4>
                    <p className="text-sm text-graphite leading-relaxed">
                      {language === 'fr' ? project.approach.fr : project.approach.en}
                    </p>
                  </div>

                  <div className="bg-charcoal p-5 rounded-xl text-white shadow-md">
                    <h4 className="text-xs uppercase tracking-widest text-amber font-bold mb-2 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber"></span>
                      {t("L'Impact", "The Impact")}
                    </h4>
                    <p className="text-sm text-warm-white/90 font-medium leading-relaxed">
                      {language === 'fr' ? project.impact.fr : project.impact.en}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={prev}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-mineral-dark hover:border-amber text-graphite hover:text-amber transition-all duration-300"
              aria-label={t("Projet précédent", "Previous project")}
            >
              <NavArrowLeft width={18} height={18} />
            </button>
            <div className="flex gap-2">
              {projects.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    i === current ? "bg-amber w-6" : "bg-mineral-dark"
                  }`}
                  aria-label={t(`Aller au projet ${i + 1}`, `Go to project ${i + 1}`)}
                />
              ))}
            </div>
            <button
              onClick={next}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-mineral-dark hover:border-amber text-graphite hover:text-amber transition-all duration-300"
              aria-label={t("Projet suivant", "Next project")}
            >
              <NavArrowRight width={18} height={18} />
            </button>
          </div>
        </div>
      </Container>
    </section>
  );
}
